import { Component, useEffect, useRef, useState, type ReactNode } from 'react';
import App from 'next/app';
import { useRouter } from 'next/router';
import type { AppContext, AppProps } from 'next/app';

import { SymphonyApp } from '../components/symphony/SymphonyApp';
import { Button, Typography } from '../components/symphony/ui';
import { ActiveConversationProvider } from '../components/chat/ActiveConversationContext';
import { resolveMount, type Mount } from '../lib/resolve-mount';
import { MountContext } from '../lib/use-base-path';
import { GenericEventsProviders } from '../lib/GenericEventsProviders';
import { installNextDataMountPrefix } from '../lib/install-next-data-mount-prefix';
import { FONT_VARS } from '../lib/fonts';
import { I18nextProvider } from '@wix/wix-i18n-config';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '../lib/query-client';
import {
  getI18n,
  activateLocale,
  resolveInitialLocale,
  isRtlLocale,
  getStoredLocale,
  setStoredLocale,
  getCachedSiteLocale,
  setCachedSiteLocale,
  loadLocaleMessages,
  localeFromCookieHeader,
  siteLocaleFromCookieHeader,
  DEFAULT_LOCALE,
  type Locale,
} from '../lib/i18n';
import { LocaleContext } from '../lib/locale-context';
import { fetchSiteLanguage, persistSiteLanguage } from '../lib/site-language';
import { SsrCountryContext } from '../lib/geo-country';
import { readCountryCode } from '../lib/edge-geo';
import { COUNTRY_DIAL } from '../lib/country-codes';
import { useInstanceAuthBootstrap } from '../lib/use-instance-bootstrap';
import { initAppsFlyer } from '../lib/appsflyer';
import { initFirebaseAnalytics } from '../lib/firebase-analytics';
import * as Sentry from '@sentry/nextjs';
import { reportClientError } from '../lib/report-client-error';
import {
  applyTheme,
  getStoredTheme,
  resolveTheme,
  setStoredTheme,
  subscribeSystemTheme,
  systemPrefersDark,
  themeFromCookieHeader,
  type Theme,
} from '../lib/theme';
import { ThemeContext } from '../lib/theme-context';
// Tokens first: globals.css and every .module.css resolve `var(--sym-*)` against
// the :root scope this defines.
import '../styles/design-tokens.css';
// The generated type scale (.sym-display/.sym-h1…p3 + .sym-type-*) — BEFORE globals so
// globals' equal-specificity marker overrides (e.g. .sym-section-title) still win on
// source order, exactly as they did when the base classes lived in globals.css.
import '../styles/typography.css';
import '../styles/globals.css';

// Before any client navigation can fire a `_next/data` fetch at the unrouted
// host root. Self-guards to the client.
installNextDataMountPrefix();

// Both of the side effects below were gated on the product name reading "Symphony",
// which was really a MOUNT test — it kept them off the manage.wix.com mounts. The brand
// axis is gone but the mount axis is not, so they are re-expressed on the mount rather
// than dropped: the same surfaces are covered as before, with no brand word involved.
// Matches the `ssrMount === ''` gate on ssrCountry below — those two must agree.
// Client-only: both call sites are an effect and an event handler.
const onSymphonyHost = (): boolean =>
  typeof window !== 'undefined' && resolveMount(window.location.pathname) === '';

type AppExtraProps = {
  // The mount the request arrived on, for `useBasePath()` under SSR — where
  // `window.location` doesn't exist and Pepyaka has already stripped the prefix.
  // `null` when no header carried the original URL.
  ssrMount: Mount | null;
  ssrLocale: Locale;
  // The first-paint theme, resolved server-side off the same cookie `_document` stamps
  // `data-theme` from — so the served HTML, this seed and the client agree on frame one.
  ssrTheme: Theme;
  // The first-paint locale's message bundle, resolved server-side and shipped in
  // `__NEXT_DATA__` so the client can build i18n in that language synchronously
  // (no English→target flicker). `null` for English (statically bundled) and on
  // client-side navigations (no request to sniff — the live instance already holds
  // the language).
  ssrMessages: Record<string, string> | null;
  // The visitor's country from the edge geo header, so the onboarding phone picker's
  // country code is right on the FIRST paint instead of correcting itself a moment
  // later. `''` when the header is absent (dev, or an unmapped IP) — the client fetch
  // in lib/geo-country.ts then covers it.
  ssrCountry: string;
};

// Brand fonts: the class also goes on <html> in pages/_document.tsx so the `--font-*`
// variables sit above where the brand tokens reference them — but next/font only EMITS
// its @font-face + variable CSS when the font is referenced from a page/_app (not
// _document-only), so FONT_VARS must be applied here too. Both use lib/fonts (same hashes).

function OrionWebApp({ Component, pageProps, ssrMount, ssrLocale, ssrTheme, ssrMessages, ssrCountry }: AppProps & AppExtraProps) {
  const router = useRouter();
  // The UI language, independent of the mount — all three surfaces are the same product
  // in the same language. Precedence lives in `resolveInitialLocale`: this browser's
  // stored Settings pick, then the account's Site Properties `language`, then the
  // browser's own. Seeded from SSR (which reads the same pick off the cookie) so the
  // first paint is already right, then refined on mount.
  const [locale, setLocale] = useState<Locale>(ssrLocale ?? DEFAULT_LOCALE);
  useEffect(() => {
    let cancelled = false;
    // The stored pick is authoritative and synchronous, so apply it before the network
    // says anything — a reload must never flash English at someone who chose Czech.
    const stored = getStoredLocale();
    const browserLanguage = typeof navigator === 'undefined' ? null : navigator.language;
    // The cached account language (last fetch's answer, mirrored to a cookie for SSR)
    // keeps this synchronous pass on the account's language too, so a reload doesn't
    // regress to the browser language while the fresh fetch is in flight.
    setLocale((prev) => {
      const next = resolveInitialLocale({ stored, siteLanguage: getCachedSiteLocale(), browserLanguage });
      return prev === next ? prev : next;
    });
    // Re-persist an existing pick so the SERVER gets the cookie on the next load (a
    // localStorage-only pick from an older build migrates here, after which SSR renders
    // it directly), and so its one-year expiry keeps rolling forward for active users.
    if (stored) setStoredLocale(stored);

    // Site Properties is the ACCOUNT-wide language — what the assistant and the digests
    // speak, and what the native apps write when the user picks there. It refines a
    // browser that has no pick of its own; it never overrides one, and a null answer
    // (unset, no tenant, 401 before the session settles, a network blip) changes nothing.
    void fetchSiteLanguage().then((fetched) => {
      // Mirror the answer for the NEXT load's SSR whatever happens to this one — a null
      // answer leaves the existing cache in place (see setCachedSiteLocale).
      setCachedSiteLocale(fetched);
      if (cancelled) return;
      setLocale((prev) => {
        const next = resolveInitialLocale({
          stored,
          // A null fetch falls back to the cache so a blip can't downgrade the sync pass above.
          siteLanguage: fetched ?? getCachedSiteLocale(),
          browserLanguage,
        });
        return prev === next ? prev : next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // The setter handed to the Settings picker: persist the pick FIRST (so a reload
  // restores it whatever the backend does), then flip the live locale (re-renders every
  // translation consumer), then mirror it to the user's meta-site Site Properties so the
  // backend assistant/digests speak the same language and the user's other surfaces pick
  // it up — best-effort, never blocks the UI switch. Root mount only, per onSymphonyHost
  // below; the local persistence is unconditional, so the pick sticks on every mount.
  const chooseLocale = (next: Locale): void => {
    setStoredLocale(next);
    setLocale(next);
    if (onSymphonyHost()) void persistSiteLanguage(next);
  };
  // Signed-instance auth bootstrap (behind the symphonyInstanceAuthWeb toggle):
  // acquire the instance on load and redirect to sign-in if there's no session.
  // Called for its side effects only; the wait is shown by the one in-pane loader
  // (DashboardLoading), which is already on screen during the bootstrap — so there
  // is no separate auth cover to hand off to (which caused a loading→empty→loading
  // flash). No-op (never gates) for users the toggle doesn't target.
  useInstanceAuthBootstrap();

  // Boot the AppsFlyer Web SDK on the client so the SDK's own pageview lands even on a
  // load that fires no events of ours. `initAppsFlyer` owns the Symphony-only gate and
  // is idempotent, so this is an eager boot, NOT the gate — every log* call also inits
  // on demand (see lib/appsflyer.ts). Do not re-add a `productName` condition here: the
  // context seeds from SSR as 'Orion' and flips post-mount, which would gate on a value
  // that isn't settled yet.
  useEffect(() => {
    initAppsFlyer();
    initFirebaseAnalytics();
  }, []);

  // The colour theme. Seeded from SSR — `_document` stamps `data-theme` into the served
  // HTML off the same cookie — so dark mode is painted on the FIRST frame and a reload
  // never flashes the light palette at someone who chose dark. With NO pick stored the
  // server cannot know the answer (no request header carries `prefers-color-scheme`), so
  // `/theme-init.js` has already corrected the attribute before this component ever runs;
  // the pass below is what puts the same verdict into React state.
  //
  // The effect re-reads `localStorage`, which outranks the cookie on the client (a pick
  // made in a browser that blocks cookies, or one made before the cookie existed), and
  // re-persists it so the SERVER has the cookie next time. Same shape as the locale pass
  // above, for the same reason. A system-derived theme is deliberately NOT persisted:
  // writing it would turn "never chose" into "chose" and stop following the OS.
  const [theme, setTheme] = useState<Theme>(ssrTheme ?? 'light');
  // Whether the toggle has been flipped in THIS session. Not the same question as "is anything
  // stored": `setStoredTheme` swallows a failed write on purpose (private mode, cookies blocked,
  // quota), so where neither store is writable a real pick reads back as `null` and the listener
  // below would happily repaint over it on the next OS flip — the app overruling a choice the user
  // just made, with no way to make it stick. Storage answers durability; this ref answers intent.
  const themeChosen = useRef(false);
  useEffect(() => {
    const stored = getStoredTheme();
    const next = resolveTheme(stored, systemPrefersDark());
    setTheme(next);
    applyTheme(next);
    if (stored) setStoredTheme(stored);
    // Until the toggle is touched, the app follows the OS LIVE — flipping macOS/Windows to dark
    // with the tab open flips the app, as it does on both phones. The guard is re-evaluated on
    // every event, so this goes inert the moment a pick exists — one made in this tab even if
    // nothing could be written down.
    return subscribeSystemTheme((system) => {
      if (themeChosen.current || getStoredTheme()) return;
      setTheme(system);
      applyTheme(system);
    });
  }, []);

  // The setter handed to the Settings toggle: persist FIRST (so a reload restores it
  // whatever else happens), then flip the live theme. `applyTheme` stamps <html>, which
  // re-resolves every --ds-* token in place — no re-render of the tree is needed for the
  // colours, only for the switch's own checked state.
  const chooseTheme = (next: Theme): void => {
    themeChosen.current = true;
    setStoredTheme(next);
    setTheme(next);
    applyTheme(next);
  };

  // Anything the browser throws outside React's tree — an async callback, a rejected
  // promise — plus a route so a Grafana line points at a screen. React render throws
  // are caught by RenderErrorBoundary below instead.
  useEffect(() => {
    const onError = (e: ErrorEvent) => reportClientError('window', e.error ?? e.message, router.pathname);
    const onRejection = (e: PromiseRejectionEvent) => reportClientError('unhandled-rejection', e.reason, router.pathname);
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, [router.pathname]);

  // Seed the instance with the SSR-resolved locale + its bundle so the server and
  // the first client render agree in that language — the seed only takes on the
  // instance's FIRST construction (server: per request; client: once per tab), so
  // it defines the first paint, and `activateLocale` below owns every switch after.
  const i18n = getI18n({ locale: ssrLocale, messages: ssrMessages });

  // Load + switch the active locale in an effect, never during render — `getI18n`
  // is side-effect-free so the render stays pure. Runs only on the client; SSR
  // renders English (the bundle can't be added inside renderToString). The
  // cleanup flag makes a superseded switch bail before it mutates the instance,
  // so the user's latest locale wins regardless of bundle-download order.
  useEffect(() => {
    let stale = false;
    void activateLocale(i18n, locale, () => stale);
    return () => {
      stale = true;
    };
  }, [i18n, locale]);

  // Reflect the language + writing direction on <html> for the a11y tree and for
  // RTL locales (Arabic, Hebrew). Layout mirroring itself is a later phase; this
  // is the correct hook for it and is harmless (dir=ltr) for every LTR locale.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = locale;
    document.documentElement.dir = isRtlLocale(locale) ? 'rtl' : 'ltr';
  }, [locale]);

  const page = <Component {...pageProps} />;

  return (
    <div className={FONT_VARS} style={{ display: 'contents' }}>
      <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <LocaleContext.Provider value={{ locale, setLocale: chooseLocale }}>
            <ThemeContext.Provider value={{ theme, setTheme: chooseTheme }}>
            <GenericEventsProviders>
              {/* `??`, never `||`: the root mount is the empty string, which is falsy. */}
              <MountContext.Provider value={ssrMount ?? '/orion-web'}>
                <SsrCountryContext.Provider value={ssrCountry}>
                  <ActiveConversationProvider>
                    {/* OUTSIDE SymphonyApp, not inside it. While this wrapped only `{page}` it
                        protected almost nothing: on the home route `page` is `<Home/>`, which
                        renders null — the entire UI (AgentNameProvider → AppFrame → DashboardHost
                        → the home) sat outside the boundary, so a throw there unmounted the tree
                        with no fallback and left the user on whatever was last painted. */}
                    <RenderErrorBoundary route={router.pathname}>
                      <SymphonyApp>{page}</SymphonyApp>
                    </RenderErrorBoundary>
                  </ActiveConversationProvider>
                </SsrCountryContext.Provider>
              </MountContext.Provider>
            </GenericEventsProviders>
            </ThemeContext.Provider>
          </LocaleContext.Provider>
        </QueryClientProvider>
      </I18nextProvider>
      </QueryClientProvider>
    </div>
  );
}

// A render throw anywhere in the tree otherwise shows a blank page and leaves no
// trace: no test in this package renders a component, so CI cannot catch it either.
// This reports it AND offers a way out: `null` was chosen when the boundary wrapped only
// the page (a null-rendering component on the home route), where there was nothing to
// recover. Now that it wraps the whole app, rendering nothing would leave the user staring
// at a dead screen with no way to tell a hung load from a crashed one — the state this
// boundary exists to make legible.
class RenderErrorBoundary extends Component<{ route: string; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(err: unknown) {
    // app_logs (the queryable line) + Sentry (grouping/alerting). Render throws are the one
    // class Sentry's global browser handlers don't see, so they're captured explicitly here.
    // Sentry.init() (instrumentation-client.ts) no-ops without a DSN/outside production, so this
    // call is always safe.
    reportClientError('render', err, this.props.route);
    Sentry.withScope((scope) => {
      scope.setTag('route', this.props.route);
      Sentry.captureException(err instanceof Error ? err : new Error(String(err)));
    });
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return <RenderErrorFallback />;
  }
}

/**
 * What a caught render throw looks like.
 *
 * The copy is a LITERAL, and this is the one place in the app where that is correct: i18n is a
 * provider above this boundary, and a fallback that calls `t()` to explain that rendering failed
 * can fail the same way. Everything else is the design system — `Button` and the tokens are a leaf
 * component and CSS custom properties, neither of which depends on the tree that threw.
 */
function RenderErrorFallback() {
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--ds-space-16)',
        minHeight: '100dvh',
        padding: 'var(--ds-space-24)',
        textAlign: 'center',
        color: 'var(--ds-text-primary)',
        background: 'var(--ds-surface-standard-bg)',
      }}
    >
      <Typography variant="p1">Something went wrong loading Symphony.</Typography>
      <Button onClick={() => window.location.reload()}>Reload</Button>
    </div>
  );
}

// SSR brand detection for crawlers. In prod Pepyaka strips `/orion-web` /
// `/symphony-web` before the pod sees the request, so `ctx.req.url` is the
// post-strip path and useless for telling the brands apart. The Wix-standard
// header `x-wix-forwarded-url` carries the pre-strip URL (host may be
// rewritten, path is preserved). The other `x-*-uri` candidates are nginx
// conventions kept as fallbacks. On the client `getInitialProps` doesn't run,
// so ssrBrand stays null and `useDetectedBrand` reads window.location directly.
OrionWebApp.getInitialProps = async (appCtx: AppContext): Promise<AppExtraProps & { pageProps: Record<string, unknown> }> => {
  const baseProps = await App.getInitialProps(appCtx);
  const req = appCtx.ctx.req as { url?: string; headers?: Record<string, string | string[] | undefined> } | undefined;
  const headers = req?.headers ?? {};
  const pickHeader = (name: string): string => {
    const v = headers[name];
    return typeof v === 'string' ? v : '';
  };
  const candidate =
    pickHeader('x-wix-forwarded-url') ||
    pickHeader('x-original-uri') ||
    pickHeader('x-forwarded-uri') ||
    pickHeader('x-wix-original-uri') ||
    req?.url ||
    '';
  // The root mount has no path prefix to key on, so it is resolved from the host —
  // pass the forwarded host explicitly (the forwarded-URL host can be rewritten by
  // the edge, so prefer the dedicated header).
  const hostHint = pickHeader('x-forwarded-host') || pickHeader('host');
  // Through the shared, host-aware helper so it can't drift from the client resolver.
  const ssrMount = candidate || hostHint ? resolveMount(candidate, hostHint) : null;
  // LOCALE seed for the first paint, over the same precedence the client uses: the
  // Settings pick (mirrored to the `orion-web.locale` cookie) OUTRANKS the account
  // language, then Accept-Language. Site Properties itself needs an RPC SSR doesn't
  // make, so its slot is filled from the `symphony-site-locale` cookie the client
  // mirrors the last fetch into — a returning account-language user paints right
  // immediately, and the client effect re-fetches + refines a moment later.
  const ssrCookie = pickHeader('cookie');
  const ssrLocale = resolveInitialLocale({
    stored: localeFromCookieHeader(ssrCookie),
    siteLanguage: siteLocaleFromCookieHeader(ssrCookie),
    browserLanguage: pickHeader('accept-language'),
  });
  // THEME seed for the first paint, off the same `orion-web.theme` cookie `_document` reads.
  // Absent = light HERE only because a request cannot see the OS; the phones' rule (absent =
  // follow the system) is applied in the browser by /theme-init.js, pre-paint, and by the effect
  // above in React state. See lib/theme.ts.
  const ssrTheme = resolveTheme(themeFromCookieHeader(ssrCookie));
  // Ship the resolved locale's bundle so the FIRST paint is already in that language (no
  // English→target flicker). Only on the server (`req` present) and only for non-English —
  // English is statically bundled, and on client-side navigations the live instance already
  // holds the language. The awaited import is the same chunk `activateLocale` would fetch,
  // so embedding it here also spares that round-trip on the initial load.
  const ssrMessages =
    req && ssrLocale !== DEFAULT_LOCALE ? await loadLocaleMessages(ssrLocale).catch(() => null) : null;
  // Only the Symphony root, mirroring lib/geo-country.ts's own gate — and only a country
  // the picker can actually select, the same guard /api/geo applies. An ISO we carry no
  // dial code for would leave the trigger reading a bare "+" while the number was being
  // built from the fallback dial.
  const edgeCountry = ssrMount === '' && req ? readCountryCode({ headers }) : '';
  const ssrCountry = COUNTRY_DIAL[edgeCountry] ? edgeCountry : '';
  return { ...baseProps, ssrMount, ssrLocale, ssrTheme, ssrMessages, ssrCountry };
};

export default OrionWebApp;
